import { IconButton } from "@carbon6/c6-frontend-design";
import { useId, type ReactNode } from "react";
import { Editor, useEditorController } from "@/components/editor";
import type { MemoController } from "./useMemoController";
import styles from "./MemoComposer.module.css";

const MEMO_MAX_LENGTH = 2000;

// 메모 작성창이다: 멀티라인 textarea + 작성 버튼. c6 에 멀티라인 입력이 없으므로 native textarea 를
// 쓴다(CodeEditor.tsx 와 같은 방식). 표면이 mutation 을 막으면 읽기 전용 안내만 그린다.
export function MemoComposer({ controller }: { controller: MemoController }): ReactNode {
  const inputId = useId();
  const { canMutate, creating } = controller;
  const editor = useEditorController({
    value: "",
    disabled: !canMutate || creating,
    onSave: async (content) => {
      const created = await controller.onCreate(content);
      return created ? { ok: true, value: "" } : { ok: false };
    },
  });

  if (!canMutate) {
    return <p className={styles.readonly}>메모 작성 권한이 없습니다 (읽기 전용).</p>;
  }

  const submitDisabled = editor.inputDisabled || editor.value.trim() === "";

  async function handleSubmit(): Promise<void> {
    await editor.save();
  }

  return (
    <div className={styles.composer}>
      <label htmlFor={inputId} className={styles.label}>
        메모 작성
      </label>
      <Editor
        controller={editor}
        id={inputId}
        className={styles.input}
        rows={3}
        maxLength={MEMO_MAX_LENGTH}
        placeholder="메모를 입력해 주세요."
        spellCheck={false}
      />
      <div className={styles.actions}>
        <span className={styles.counter}>
          {editor.value.length}/{MEMO_MAX_LENGTH}
        </span>
        <IconButton
          text={editor.saving || creating ? "작성 중…" : "작성"}
          variant="solid"
          tone="primary"
          disabled={submitDisabled}
          onClick={() => void handleSubmit()}
        />
      </div>
    </div>
  );
}
